import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RotatingLines } from "react-loader-spinner";
import { useFulfillListQuery, useBatchEvaluateExecutionsMutation, useBulkDownloadFulfillImagesMutation } from "src/api/fulfillAPI";
import { useSelectGroupQuery } from "src/api/userAPI";
import { useAppSelector } from "src/store/actions/action";
import {
  deleteTypeSearch,
  setListData,
  setListDataInit,
  toggleExecuteSelect,
  setAllExecutesSelected,
  clearExecuteSelection,
  setSelectedExecuteIds
} from "src/store/reducers/fulfillListReduce";
import { setRedeemImageList, setFulFillId, setRedeemDataAllInit } from "src/store/reducers/redeemReduce";
import reuseQueryString from "src/utils/reuseQueryString";
import { getPathValue } from "src/utils/queryStringUtil";
import Body from "src/components/common/Body";
import Box from "src/components/common/Box";
import Button from "src/components/common/Button";
import IconButton from "src/components/common/IconButton";
import SVG from "src/utils/SVG";
import SearchInput from "src/components/common/SearchInput";
import List from "src/components/common/List";
import SearchSortItem from "src/components/filter/SearchSortItem";
import FulFillShopItem from "src/components/notice/FulFillShopItem";
import FulfillFilterItem from "src/components/filter/FulfillFilterItem";
import PaginationCommon from "src/components/common/PaginationCommon";
import BottomModal, { RefProps } from "src/components/common/BottomModal";
import styled from "styled-components";
import Chip from "src/components/common/Chip";
import { useGetFulfilExcelMutation, useGetS3UrlMutation } from "../../api/noticeAPI";
import CheckBox from "src/components/common/CheckBox";
import ScoreRegisterBox from "src/components/notice/ScoreRegisterBox";
import ExecuteFeedbackUploadModal from "src/components/notice/ExecuteFeedbackUploadModal";
import { RefProps as BottomModalV2RefProps } from "src/components/common/BottomModalV2";

// 그룹채널 타입 정의
interface GroupChannel {
  group_name: string;
  group_part_idx: number | string;
  group_purpose?: string;
}

// 필터 아이템 타입 정의
interface FilterItem {
  type: string;
  group_part_idx?: number | string;
  checked: boolean;
}

// 이미지 타입 정의
interface ExecuteImage {
  execute_img_idx: number;
  img_name?: string;
  img_origin_name?: string;
  img_url: string;
  ext?: string;
  type?: string;
}

// 스토어 데이터 타입 정의
interface StoreData {
  execute_idx: number;
  execute_imgs: ExecuteImage[];
  [key: string]: any;
}

// 이미지 리스트 아이템 타입 정의 (Image 타입과 동일)
interface ImageListItem {
  img_idx: number;
  img_name: string;
  img_origin_name: string;
  img_url: string;
  ext: string;
  type: null;
}

const initFilterData: any = {
  filterList: {
    fulfillFilter: {
      title: "이행 여부",
      data: [
        { type: "이행", checked: false },
        { type: "미이행", checked: false },
      ],
    },
    assessFilter: {
      title: "평가 여부",
      data: [
        { type: "평가 전", checked: false },
        { type: "평가 완료", checked: false },
      ],
    },
    typeFilter: {
      title: "매장 유형",
      data: [
        { type: "직영점", checked: false },
        { type: "대리점", checked: false },
        { type: "백화점", checked: false },
        { type: "면세점", checked: false },
        { type: "상설", checked: false },
        { type: "아울렛", checked: false },
      ],
    },
    groupChannelFilter: {
      title: "그룹채널",
      data: [],
    },
  },
  applyFilterList: {},
  fulfillSearchArray: [],
  keywordSearchData: "",
  sortSearchData: "최신순",
  sortSearchIdx: 0,
  reExecuteData: "",
  listTypeData: "",
};

function fulfillList() {
  // 글로벌
  const navigate = useNavigate();
  const navigateState = useLocation();
  const dispatch = useDispatch();
  const noId: any = getPathValue(); // 이행번호
  const fulfillListReduce = useAppSelector((state) => state.fulfillListReduce);
  const storeCurrentPage = useAppSelector((state) => state.fulfillListReduce.currentPageNumber);
  const userInfo = useAppSelector((state) => state.userReduce.userInfo);
  const selectedExecuteIds = useAppSelector((state) => state.fulfillListReduce.selectedExecuteIds) || [];
  const isAllSelected = useAppSelector((state) => state.fulfillListReduce.isAllSelected) || false;

  // 이행목록 페이지 최초 진입 시 재이행 선택 이미지 초기화
  useEffect(() => {
    dispatch(setRedeemDataAllInit());
  }, []); // 빈 dependency array로 컴포넌트 마운트 시에만 실행

  // 선택된 그룹채널의 group_part_idx 배열을 추출하는 helper 함수
  const getSelectedGroupChannelIds = (): (number | string)[] => {
    const selectedGroupChannels = fulfillListReduce.applyFilterList?.groupChannelFilter?.data?.filter((item: FilterItem) => item.checked) || [];
    return selectedGroupChannels.map((item: FilterItem) => item.group_part_idx);
  };

  const [reToggle, setReToggle] = useState(fulfillListReduce.reExecuteData === "Y" ? true : false);
  const [listType, setListType] = useState(userInfo.user_type === "S" ? "gallery" : fulfillListReduce.listTypeData); // list, gallery

  const [api, setApi] = useState({
    apiSkip: true,
    filteredData: "",
  });
  const { apiSkip, filteredData } = api;

  const [queryString, setQueryString] = useState<any>({
    fulfillSearch: fulfillListReduce.fulfillSearchArray, // 이행여부, 평가여부, 매장유형, 그룹채널
    contentsSearchString: fulfillListReduce.keywordSearchData, // 검색어
    fulfillSortSearchString: fulfillListReduce.sortSearchData, // sort
    reExecuteSearchString: fulfillListReduce.reExecuteData, // 재이행 여부
    pageSearchNumber: 0, // page
    groupChannelIdx: [], // 그룹채널 필터 group_part_idx
  });

  const [currentPage, setCurrentPage] = useState<number | any>();
  const [totalPages, setTotalPages] = useState();

  // ================================================================================================
  // API 영역
  // ================================================================================================
  const fulfillList = useFulfillListQuery({ notice_idx: noId, search: filteredData }, { skip: apiSkip });
  
  // 그룹채널 목록 조회 API
  const groupChannelAPI = useSelectGroupQuery({ userId: userInfo.user_id, type: "S" });

  const [getExcelData] = useGetFulfilExcelMutation();
  const [batchEvaluate, { isLoading: isBatchEvaluating }] = useBatchEvaluateExecutionsMutation();
  const [bulkDownload] = useBulkDownloadFulfillImagesMutation();

  // 쿼리스트링 변경에 따른 api 호출
  useEffect(() => {
    if (queryString) {
      const searchFilter = reuseQueryString(queryString);
      setApi({
        ...api,
        filteredData: searchFilter,
        apiSkip: false,
      });
    }
  }, [queryString]);

  // ================================================================================================
  // 기능별 개발 영역
  // ================================================================================================
  // 재이행만 필터
  const searchReExecute = () => {
    const groupChannelIdxArray = getSelectedGroupChannelIds();
    
    if (reToggle) {
      // init : false -> 재이행만 해제시 모두 나와야함
      setQueryString({ ...queryString, reExecuteSearchString: "", pageSearchNumber: 1, groupChannelIdx: groupChannelIdxArray });
      dispatch(
        setListData({
          reExecuteData: "",
          currentPageNumber: 1,
        }),
      );
    } else {
      setQueryString({ ...queryString, reExecuteSearchString: "Y", pageSearchNumber: 1, groupChannelIdx: groupChannelIdxArray });
      dispatch(
        setListData({
          reExecuteData: "Y",
          currentPageNumber: 1,
        }),
      );
    }
    setCurrentPage(1);
    setReToggle(!reToggle);
  };

  // 페이지네이션
  useEffect(() => {
    setCurrentPage(storeCurrentPage);
    const groupChannelIdxArray = getSelectedGroupChannelIds();

    // 페이지 변경 시 선택 초기화
    dispatch(clearExecuteSelection());

    setQueryString({
      ...queryString,
      pageSearchNumber: storeCurrentPage,
      filterList: fulfillListReduce.applyFilterList,
      fulfillSearch: fulfillListReduce.fulfillSearchArray,
      groupChannelIdx: groupChannelIdxArray,
    });
  }, [currentPage]);

  useEffect(() => {
    if (fulfillList.isSuccess) {
      setTotalPages(fulfillList.data.data.meta.totalPages);
    }
  }, [fulfillList]);

  const objectChange = (currentPage: number) => {
    setCurrentPage(Number(currentPage));
    dispatch(
      setListData({
        currentPageNumber: currentPage,
      }),
    );
    
    const groupChannelIdxArray = getSelectedGroupChannelIds();
    
    setQueryString({
      ...queryString,
      pageSearchNumber: currentPage,
      groupChannelIdx: groupChannelIdxArray,
    });
  };

  // 키워드 검색
  const [searchKeyword, setSearchKeyword] = useState(fulfillListReduce.keywordSearchData ? fulfillListReduce.keywordSearchData : "");

  const onSearchKeywords = (e: any) => {
    const contents = e.currentTarget.parentElement?.childNodes[0].value;
    setSearchKeyword(contents);
    setCurrentPage(1);
    dispatch(
      setListData({
        keywordSearchData: contents,
        currentPageNumber: 1,
      }),
    );
    
    const groupChannelIdxArray = getSelectedGroupChannelIds();
    
    setQueryString({
      ...queryString,
      pageSearchNumber: 1,
      contentsSearchString: contents,
      groupChannelIdx: groupChannelIdxArray,
    });
  };

  // 키워드 엔터로 검색
  const onKeyPress = (e: any) => {
    if (e.key === "Enter") {
      onSearchKeywords(e);
    }
  };

  // 검색어 삭제
  const deleteSearchContents = () => {
    if (searchKeyword) {
      setCurrentPage(1);
      setSearchKeyword("");
      dispatch(
        setListData({
          keywordSearchData: "",
        }),
      );
      
      const groupChannelIdxArray = getSelectedGroupChannelIds();
      
      setQueryString({
        ...queryString,
        pageSearchNumber: 1,
        contentsSearchString: "",
        groupChannelIdx: groupChannelIdxArray,
      });
    }
  };

  // 필터모달 관련
  const filterModalRef = React.useRef<RefProps>(null);
  const [filterData, setFilterData] = useState<any>(initFilterData);
  const [applyFilterData, setApplyFilterData] = useState<any>(initFilterData);
  const [applyFilterItem, setApplyFilterItem] = useState(fulfillListReduce.fulfillSearchArray);
  const [initVisible, setInitVisible] = useState<boolean>(false);

  useEffect(() => {
    setFilterData(fulfillListReduce.filterList);
    setApplyFilterData(fulfillListReduce.applyFilterList);
    setApplyFilterItem(fulfillListReduce.fulfillSearchArray);
  }, [fulfillListReduce]);

  // 그룹채널 데이터 Redux store 설정 (group_part_idx 포함, group_purpose가 비어있는 것들만)
  useEffect(() => {
    if (groupChannelAPI.isSuccess && groupChannelAPI.data) {
      // group_purpose가 비어있거나 null인 것들만 필터링
      const filteredGroups = groupChannelAPI.data.filter((group: GroupChannel) => 
        !group.group_purpose?.trim()
      );
      
      const groupChannelData = filteredGroups.map((group: GroupChannel) => ({
        type: group.group_name,
        group_part_idx: group.group_part_idx, // group_part_idx 추가
        checked: false,
      }));
      
      // 기존 선택된 항목들 유지
      const prevSelectedGroups = fulfillListReduce.filterList?.groupChannelFilter?.data?.filter((item: FilterItem) => item.checked) || [];
      const updatedGroupChannelData = groupChannelData.map((newItem: FilterItem) => {
        const prevItem = prevSelectedGroups.find((prev: FilterItem) => prev.type === newItem.type);
        return prevItem ? { ...newItem, checked: prevItem.checked } : newItem;
      });

      dispatch(
        setListData({
          filterList: {
            ...fulfillListReduce.filterList,
            groupChannelFilter: {
              title: "그룹채널",
              data: updatedGroupChannelData,
            },
          },
          applyFilterList: {
            ...fulfillListReduce.applyFilterList,
            groupChannelFilter: {
              title: "그룹채널",
              data: updatedGroupChannelData,
            },
          },
        }),
      );
    }
  }, [groupChannelAPI.isSuccess, groupChannelAPI.data]);

  const openFilterModal = useCallback(() => {
    if (filterModalRef.current) filterModalRef.current.modalOpen();
  }, []);

  const initBtn = () => {
    // fulfillListReduce.filterList만 initState로 초기화
    dispatch(setListDataInit());
  };

  const setInitFunc = (propsSelectedFliterCheck: boolean) => {
    setInitVisible(propsSelectedFliterCheck);
  };

  const confirmBtn = () => {
    setApplyFilterItem(fulfillListReduce.fulfillSearchArray);
    
    // 선택된 그룹채널의 group_part_idx 추출
    const selectedGroupChannels = fulfillListReduce.filterList?.groupChannelFilter?.data?.filter((item: any) => item.checked) || [];
    const groupChannelIdxArray = selectedGroupChannels.map((item: any) => item.group_part_idx);
    
    dispatch(
      setListData({
        applyFilterList: fulfillListReduce.filterList,
        fulfillSearchArray: fulfillListReduce.fulfillSearchArray,
        currentPageNumber: 1,
      }),
    );
    setQueryString({
      ...queryString,
      pageSearchNumber: 1,
      fulfillSearch: fulfillListReduce.fulfillSearchArray,
      groupChannelIdx: groupChannelIdxArray,
    });
    setCurrentPage(1);
    if (filterModalRef.current) filterModalRef.current.modalClose();
  };

  const closeBtn = () => {
    dispatch(
      setListData({
        filterList: fulfillListReduce.applyFilterList,
        fulfillSearchArray: fulfillListReduce.fulfillSearchArray,
      }),
    );
    if (filterModalRef.current) filterModalRef.current.modalClose();
  };

  // 필터 선택시 박스 스크롤 끝으로 이동
  const filterBoxRef = React.useRef<HTMLDivElement>(null);
  const [filterSelected, setFilterSelected] = useState(false);
  useEffect(() => {
    if (filterBoxRef.current && filterSelected) {
      const scrollX = filterBoxRef.current.scrollLeft;
      const scrollXWidth = filterBoxRef.current.scrollWidth;
      filterBoxRef.current.scrollTo({ top: 0, left: scrollX + scrollXWidth, behavior: "smooth" });
      setFilterSelected(false);
    }
    setFilterSelected(true);
  }, [applyFilterItem]);

  // 필터 칩 삭제
  const deleteSelected = (v: any, i: number) => {
    // store에 typeSearchArray(선택된요소) 지워주기 : 필터 옆 chip 제거
    dispatch(deleteTypeSearch(v));
    const remainChips = fulfillListReduce.fulfillSearchArray?.filter((type) => type !== v);
    const deletedChips = fulfillListReduce.fulfillSearchArray?.filter((type) => type === v).toString();

    setApplyFilterItem(remainChips);

    const getSelectedFulfill = applyFilterData.fulfillFilter.data.map((value: any) =>
      value.type === deletedChips ? { ...value, checked: false } : { ...value },
    );
    const getSelectedAssess = applyFilterData.assessFilter.data.map((value: any) =>
      value.type === deletedChips ? { ...value, checked: false } : { ...value },
    );
    const getSelectedType = applyFilterData.typeFilter.data.map((value: any) => (value.type === deletedChips ? { ...value, checked: false } : { ...value }));
    
    // 그룹채널 필터 처리 - 삭제된 항목의 checked 상태 업데이트
    const getSelectedGroupChannel = applyFilterData.groupChannelFilter?.data?.map((value: any) => 
      value.type === deletedChips ? { ...value, checked: false } : { ...value }
    ) || [];
    
    // 업데이트된 그룹채널에서 선택된 항목들의 group_part_idx 추출
    const selectedGroupChannels = getSelectedGroupChannel.filter((item: any) => item.checked);
    const groupChannelIdxArray = selectedGroupChannels.map((item: any) => item.group_part_idx);

    dispatch(
      setListData({
        filterList: {
          fulfillFilter: {
            title: "이행 여부",
            data: getSelectedFulfill,
          },
          assessFilter: {
            title: "평가 여부",
            data: getSelectedAssess,
          },
          typeFilter: {
            title: "매장 유형",
            data: getSelectedType,
          },
          groupChannelFilter: {
            title: "그룹채널",
            data: getSelectedGroupChannel,
          },
        },
        applyFilterList: {
          fulfillFilter: {
            title: "이행 여부",
            data: getSelectedFulfill,
          },
          assessFilter: {
            title: "평가 여부",
            data: getSelectedAssess,
          },
          typeFilter: {
            title: "매장 유형",
            data: getSelectedType,
          },
          groupChannelFilter: {
            title: "그룹채널",
            data: getSelectedGroupChannel,
          },
        },
        fulfillSearchArray: remainChips,
        currentPageNumber: 1,
      }),
    );

    setQueryString({
      ...queryString,
      pageSearchNumber: 1,
      fulfillSearch: remainChips,
      groupChannelIdx: groupChannelIdxArray, // 그룹채널 필터 업데이트
    });
    setCurrentPage(1);
  };

  // 정렬모달 관련
  const [sortIdx, setSortIdx] = useState<number>(fulfillListReduce.sortSearchIdx ? fulfillListReduce.sortSearchIdx : 0);
  const [sortName, setSortName] = useState<string>(fulfillListReduce.sortSearchData ? fulfillListReduce.sortSearchData : "최신순");
  const sortModalRef = React.useRef<RefProps>(null);

  const openSortModal = useCallback(() => {
    if (sortModalRef.current) sortModalRef.current.modalOpen();
  }, []);

  const sortBtn = async (e: any) => {
    setCurrentPage(1);
    const sortName = e.target.textContent;
    setSortName(sortName);
    dispatch(
      setListData({
        sortSearchData: sortName,
        currentPageNumber: 1,
      }),
    );
    
    const groupChannelIdxArray = getSelectedGroupChannelIds();
    
    setQueryString({
      ...queryString,
      // pageSearchNumber: 1,
      fulfillSortSearchString: sortName,
      groupChannelIdx: groupChannelIdxArray,
    });
    if (sortModalRef.current) sortModalRef.current.modalClose();
  };

  const childrenSortIdxFunc = (idx: number) => {
    setSortIdx(idx);
    dispatch(
      setListData({
        sortSearchIdx: idx,
      }),
    );
  };

  const handleClick = async () => {
    try {
      await getExcelData(noId);
    } catch (e) {
      console.log(e);
    }
  };
  // 리스트 뷰 타입 변경
  const changeList = () => {
    setListType("list");
    dispatch(
      setListData({
        listTypeData: "list",
      }),
    );
  };
  const changeGrid = () => {
    setListType("grid");
    dispatch(
      setListData({
        listTypeData: "grid",
      }),
    );
  };
  const changeGallery = () => {
    setListType("gallery");
    dispatch(
      setListData({
        listTypeData: "gallery",
      }),
    );
  };

  // 일괄 평가 관련 상태
  const batchEvaluateModalRef = React.useRef<RefProps>(null);
  const assessData = useAppSelector((state) => state.fulfillDetailReduce.assessData);

  // 일괄 다운로드 관련 상태
  const [isDownloading, setIsDownloading] = useState(false);

  // 이행평가 피드백 업로드 (VMD-389)
  const feedbackUploadModalRef = React.useRef<BottomModalV2RefProps>(null);
  const openFeedbackUploadModal = () => feedbackUploadModalRef.current?.modalOpen();

  // 매장 선택 핸들러
  const handleExecuteSelect = (executeId: number) => {
    dispatch(toggleExecuteSelect(executeId));
  };

  // 전체 선택 핸들러 — 선택은 전 매장 가능 (평가 가능 여부는 평가 시점에 필터링)
  const handleSelectAll = () => {
    if (fulfillList.isSuccess && fulfillList.data.data.data) {
      const allStores = fulfillList.data.data.data;
      if (isAllSelected) {
        dispatch(clearExecuteSelection());
      } else {
        const executeIds = allStores.map((store: any) => store.execute_idx);
        dispatch(setAllExecutesSelected(executeIds));
      }
    }
  };

  // 선택된 매장 중 실제 평가 가능한 매장 (이행 완료 & 재이행 중 아님)
  const getEvaluableSelectedIds = (): number[] => {
    const allStores: any[] = (fulfillList.isSuccess && fulfillList.data.data.data) || [];
    return allStores
      .filter(
        (store) =>
          selectedExecuteIds.includes(store.execute_idx) &&
          store.execute_yn === "Y" &&
          store.re_execute_yn !== "Y",
      )
      .map((store) => store.execute_idx);
  };

  // 선택된 매장 중 실제 평가 가능한 매장 수 (평가하기 버튼 라벨/활성 상태에 사용)
  const evaluableCount = getEvaluableSelectedIds().length;

  // 일괄 평가 버튼 클릭
  const handleBatchEvaluate = () => {
    if (selectedExecuteIds.length === 0) {
      alert("평가할 매장을 선택해주세요.");
      return;
    }

    const evaluableIds = getEvaluableSelectedIds();
    if (evaluableIds.length === 0) {
      alert("평가 가능한 매장이 없습니다.");
      return;
    }

    const confirmMessage = `총 ${evaluableIds.length}개 매장을 평가하시겠습니까?`;
    if (window.confirm(confirmMessage)) {
      if (batchEvaluateModalRef.current) batchEvaluateModalRef.current.modalOpen();
    }
  };

  // 일괄 평가 제출
  const handleBatchEvaluateSubmit = async () => {
    if (assessData.comment === "" && assessData.score === 0) {
      alert("별점 0점일 경우 코멘트 작성은 필수입니다.");
      return;
    }

    try {
      const evaluableIds = getEvaluableSelectedIds();
      const data = {
        notice_idx: noId,
        executeIds: evaluableIds,
        score: assessData.score,
        assess_contents: assessData.comment || ""
      };

      await batchEvaluate(data).unwrap();

      if (batchEvaluateModalRef.current) batchEvaluateModalRef.current.modalClose();
      dispatch(clearExecuteSelection());

      // RTK Query의 fulfillList를 강제로 refetch하여 별점 실시간 업데이트
      await fulfillList.refetch();
    } catch (error) {
      alert("평가 처리 중 오류가 발생했습니다.");
      console.error(error);
    }
  };

  // 일괄 다운로드 버튼 클릭 - 선택 매장의 사진을 매장별 폴더로 묶은 zip으로 다운로드 (서버사이드 zip)
  const handleBatchDownload = async () => {
    if (selectedExecuteIds.length === 0) {
      alert("다운로드할 매장을 선택해주세요.");
      return;
    }

    setIsDownloading(true);
    try {
      await bulkDownload({ executeIds: selectedExecuteIds }).unwrap();
      dispatch(clearExecuteSelection());
    } catch (error) {
      console.error("일괄 다운로드 실패:", error);
      alert("다운로드 처리 중 오류가 발생했습니다.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {isDownloading && (
        <DownloadOverlay>
          <div className="spinner-box">
            <RotatingLines width="40" strokeColor="#00263e" strokeWidth="3" />
            <p>사진을 다운로드하고 있습니다.</p>
          </div>
        </DownloadOverlay>
      )}

      <PageHeader>
        <div>
          <Button variant="text" className="btn-back" onClick={() => navigate(-1)}>
            이행목록
          </Button>
        </div>
      </PageHeader>

      <Body>
        <Box pd="20px 15px">
          <SearchInput
            type="text"
            id="search-input"
            placeholder="검색어를 입력해주세요."
            defaultValue={searchKeyword}
            onClick={(e) => onSearchKeywords(e)}
            onKeyPress={(e) => onKeyPress(e)}
            deleteSearchContentFunc={deleteSearchContents}
          />
        </Box>

        <FilterBtnSet ref={filterBoxRef}>
          <Button variant="contained" br="round" color="secondary" size="medium" onClick={openFilterModal}>
            <SVG name="filter" width={16} height={16} viewBox="0 0 16 16" />
            필터
          </Button>
          <Button variant="contained" br="round" color="secondary" size="medium" onClick={openSortModal}>
            <SVG name="sort" width={14.637} height={16.348} viewBox="0 0 14.637 16.348" />
            {sortName}
          </Button>
          <Button className="only-new" variant="contained" br="round" color={reToggle ? "active" : "secondary"} size="medium" onClick={searchReExecute}>
            {reToggle ? (
              <SVG name="toggleActive" width={16} height={16} viewBox="0 0 16 16" />
            ) : (
              <SVG name="toggle" width={16} height={16} viewBox="0 0 16 16" />
            )}
            재이행만
          </Button>
          {applyFilterItem &&
            applyFilterItem.map((v: any, i: number) => {
              return (
                <Chip variant="contained" deleted={true} key={i} onDelete={() => deleteSelected(v, i)}>
                  {v}
                </Chip>
              );
            })}
        </FilterBtnSet>

        <TitleBox>
          <div className="num">
            총 <b>{fulfillList.isSuccess && fulfillList.data.data.meta.totalItems}</b>건
          </div>
          <div className="right-area">
            {userInfo.user_type === "E" && !window.ReactNativeWebView && (
              <>
                <GhostIconBtn
                  type="button"
                  aria-label="매장목록 내려받기"
                  $active
                  $activeColor="#217346"
                  onClick={handleClick}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
                    <line x1="10" y1="3.5" x2="10" y2="20.5" />
                    <line x1="3.5" y1="9" x2="20.5" y2="9" />
                    <line x1="3.5" y1="15" x2="20.5" y2="15" />
                    <circle cx="17.6" cy="17.6" r="7.2" fill="#fff" stroke="none" />
                    <circle cx="17.6" cy="17.6" r="6" fill="currentColor" stroke="none" />
                    <line x1="17.6" y1="14.7" x2="17.6" y2="20.3" stroke="#fff" strokeWidth="1.5" />
                    <polyline points="15.3 18 17.6 20.3 19.9 18" stroke="#fff" strokeWidth="1.5" />
                  </svg>
                </GhostIconBtn>
                <GhostIconBtn
                  type="button"
                  aria-label="이행평가 피드백 업로드"
                  title="이행평가 피드백 업로드"
                  $active
                  $activeColor="#00263e"
                  onClick={openFeedbackUploadModal}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 13.5V17a2 2 0 0 1-2 2H9l-4.5 3v-3H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h7" />
                    <circle cx="17.6" cy="6.4" r="5.2" fill="currentColor" stroke="none" />
                    <line x1="17.6" y1="9.2" x2="17.6" y2="3.8" stroke="#fff" strokeWidth="1.5" />
                    <polyline points="15.3 6 17.6 3.7 19.9 6" stroke="#fff" strokeWidth="1.5" />
                  </svg>
                </GhostIconBtn>
                <BarDivider />
              </>
            )}
            <ul>
              {userInfo.user_type === "E" && (
                <>
                  <li>
                    <IconButton btnName="리스트형 보기" className="logout" onClick={changeList}>
                      <SVG
                        name="listView"
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        style={{ color: listType === "list" ? "#000" : "#B9BEBE" }}
                      />
                    </IconButton>
                  </li>
                  <li>
                    <IconButton btnName="그리드형 보기" className="logout" onClick={changeGrid}>
                      <SVG
                        name="gridView"
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        style={{ color: listType === "grid" ? "#000" : "#B9BEBE" }}
                      />
                    </IconButton>
                  </li>
                  <li>
                    <IconButton btnName="갤러리형 보기" className="logout" onClick={changeGallery}>
                      <SVG
                        name="galleryView"
                        width={24}
                        height={24}
                        viewBox="0 -1 25 25"
                        style={{ color: listType === "gallery" ? "#000" : "#B9BEBE" }}
                      />
                    </IconButton>
                  </li>
                </>
              )}
            </ul>
          </div>
        </TitleBox>

        {userInfo.user_type === "E" && (
          <SelectionBar>
            <div className="left-area">
              <SelectAllCheckbox>
                <CheckBox
                  id="select-all"
                  type="round"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  label="전체 선택"
                />
              </SelectAllCheckbox>
              {selectedExecuteIds.length > 0 && (
                <SelectedCount>· {selectedExecuteIds.length}개 선택</SelectedCount>
              )}
            </div>
            <div className="right-area">
              <PhotoDownloadButton
                type="button"
                $active={selectedExecuteIds.length > 0}
                disabled={selectedExecuteIds.length === 0}
                onClick={handleBatchDownload}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 3v11" />
                  <path d="m7 11 5 5 5-5" />
                  <path d="M5 21h14" />
                </svg>
                사진{selectedExecuteIds.length > 0 ? ` (${selectedExecuteIds.length})` : ""}
              </PhotoDownloadButton>
              <EvalButton
                type="button"
                $active={evaluableCount > 0}
                disabled={evaluableCount === 0}
                onClick={handleBatchEvaluate}
              >
                평가하기{evaluableCount > 0 ? ` (${evaluableCount})` : ""}
              </EvalButton>
            </div>
          </SelectionBar>
        )}

        <FulfillShopList className={listType}>
          <FulFillShopItem
            type={listType}
            data={fulfillList.isSuccess && fulfillList.data.data}
            onExecuteSelect={handleExecuteSelect}
            selectedExecuteIds={selectedExecuteIds}
            isAllSelected={isAllSelected}
          />
        </FulfillShopList>

        {totalPages !== undefined && totalPages > 1 && (
          <PaginationCommon item_count={20} current_page={currentPage} total_page={totalPages} objectChange={objectChange} />
        )}
      </Body>

      <BottomModal
        ref={filterModalRef}
        onClick={() => initBtn()}
        onClick2={() => confirmBtn()}
        onClick3={() => closeBtn()}
        modalTitle="검색 필터"
        headerBtn="초기화"
        footerBtn="적용"
        dimClickFun={closeBtn}
        initVisible={initVisible}
      >
        <List pd="0 15px" itemPd="20px 0">
          <FulfillFilterItem filterList={filterData} setInitFunc={(filter: any) => setInitFunc(filter)} />
        </List>
      </BottomModal>

      <BottomModal modalTitle="정렬" ref={sortModalRef}>
        <List pd="0 15px">
          <SearchSortItem
            sortData={["최신순", "오래된순", "매장명순", "별점높은순", "별점낮은순"]}
            onClick={(e) => sortBtn(e)}
            initIdx={sortIdx}
            parentSortIdxFunc={childrenSortIdxFunc}
          />
        </List>
      </BottomModal>

      {/* 일괄 평가 모달 */}
      <BottomModal
        ref={batchEvaluateModalRef}
        onClick2={handleBatchEvaluateSubmit}
        modalTitle="별점 등록"
        footerBtn="등록"
        dimClickFun={() => {
          if (batchEvaluateModalRef.current) batchEvaluateModalRef.current.modalClose();
        }}
      >
        <ScoreRegisterBox star={0} value="" />
      </BottomModal>

      {/* 이행평가 피드백 업로드 모달 (VMD-389) */}
      {userInfo.user_type === "E" && !window.ReactNativeWebView && (
        <ExecuteFeedbackUploadModal ref={feedbackUploadModalRef} noticeIdx={noId} onRegistered={() => fulfillList.refetch()} />
      )}
    </>
  );
}

const PageHeader = styled.header`
  .btn-back {
    background-position: 0px 2px;
    background-size: 24px 24px;
  }
`;

const FilterBtnSet = styled.div`
  overflow: hidden;
  overflow-x: scroll;
  width: 100%;
  padding: 15px;
  border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start")}
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
  > * {
    flex: 0 0 auto;
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
  > button {
    > svg {
      position: relative;
      top: -1px;
    }
  }
  .only-new {
    > svg {
      top: 2px;
      left: 2px;
    }
  }
`;

const TitleBox = styled.div`
  ${({ theme }) => theme.flexBox.flexMixin()}
  padding: 12px 15px;
  .num {
    margin-left: 4px;
    color: #8a9099;
    font-size: 13px;
    b {
      color: #4b5158;
      font-weight: 700;
    }
  }
  .right-area {
    display: flex;
    align-items: center;
    gap: 12px;
    button {
      display: flex;
      align-items: center;
    }
    svg {
      display: block;
    }
    ul {
      ${({ theme }) => theme.flexBox.flexMixin()}
      align-items: center;
      > li {
        position: relative;
        display: flex;
        align-items: center;
        margin-left: 12px;
        padding-left: 12px;
        &:before {
          content: "";
          position: absolute;
          top: 50%;
          left: 0;
          display: inline-block;
          width: 1px;
          height: 16px;
          margin-top: -8px;
          background-color: ${({ theme }) => theme.color.lineSecondary};
        }
        &:first-child {
          margin-left: 0;
          padding-left: 0;
          &:before {
            display: none;
          }
        }
      }
    }
  }
  & + .list {
    border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  }
  & + .grid {
    border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  }
  & + .gallery {
    border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  }
`;

const FulfillShopList = styled.ul`
  &.list {
    width: 100%;
    padding: 8px 15px;
  }
  &.grid {
    padding: 24px 1px 0 15px;
    ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start", "flex-start", "wrap")}
    > li {
      width: 50%;
      padding: 0 14px 24px 0;
    }

    ${/* PC 화면에서는 4열로 표시 */ ""}
    @media (min-width: 769px) {
      > li {
        width: 25%;
      }
    }

    ${/* 넓은 화면에서는 6열로 표시 */ ""}
    @media (min-width: 1280px) {
      > li {
        width: 16.6667%;
      }
    }
  }
  &.gallery {
    width: 100%;
    padding: 8px 15px;
  }
`;

const SelectAllCheckbox = styled.div`
  display: flex;
  align-items: center;

  > span {
    display: inline-flex;
    align-items: center;
  }
  label {
    display: inline-flex !important;
    align-items: center;
    gap: 8px;
    padding-left: 0 !important;
    line-height: 1 !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: #4b5158 !important;
    > svg {
      position: static !important;
      transform: none !important;
      width: 22px !important;
      height: 22px !important;
    }
  }
`;

const SelectedCount = styled.span`
  font-size: 13px;
  color: #9aa1ab;
`;

const GhostIconBtn = styled.button<{ $active?: boolean; $activeColor?: string; $inactiveColor?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${({ $active }) => ($active ? "#e7eaee" : "#eef0f2")};
  background: ${({ $active }) => ($active ? "#fdfdfe" : "#ffffff")};
  border-radius: 8px;
  color: ${({ $active, $activeColor, $inactiveColor }) => ($active ? $activeColor || "#000" : $inactiveColor || "#c4c9cf")};
  cursor: ${({ $active }) => ($active ? "pointer" : "not-allowed")};
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: ${({ $active }) => ($active ? "#f4f5f7" : "#ffffff")};
  }
  &:active {
    background: #eef0f3;
  }
  > svg {
    display: block;
  }
  &[aria-label]:hover::after {
    content: attr(aria-label);
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    padding: 5px 9px;
    background: #17181a;
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    line-height: 1;
    border-radius: 5px;
    pointer-events: none;
    z-index: 20;
  }
`;

const BarDivider = styled.span`
  flex-shrink: 0;
  width: 1px;
  height: 20px;
  margin: 0 2px;
  background: #e6e8eb;
`;

const PhotoDownloadButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 8px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid ${({ $active }) => ($active ? "#c9ced5" : "#e6e8eb")};
  background: #fff;
  color: ${({ $active }) => ($active ? "#14284d" : "#adb3ba")};
  cursor: ${({ $active }) => ($active ? "pointer" : "not-allowed")};
  transition: background 0.15s ease, border-color 0.15s ease;
  > svg {
    flex-shrink: 0;
  }
  &:hover {
    background: ${({ $active }) => ($active ? "#f7f8fa" : "#fff")};
  }
`;

const EvalButton = styled.button<{ $active?: boolean }>`
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: ${({ $active }) => ($active ? "pointer" : "not-allowed")};
  background: ${({ $active }) => ($active ? "#14284d" : "#edeff2")};
  color: ${({ $active }) => ($active ? "#fff" : "#adb3ba")};
`;

const DownloadOverlay = styled.div`
  z-index: 2000;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);

  .spinner-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px 32px;
    background: #fff;
    border-radius: 8px;

    p {
      color: ${({ theme }) => theme.color.main};
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

const SelectionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-top: 1px solid ${({ theme }) => theme.color.linePrimary};

  .left-area {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .right-area {
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

export default fulfillList;
